/**
 * FB-2026-0016: ปรับราคาเช่าตอนย้ายอุปกรณ์ไปโครงการอื่น (มติ PO 3 พ.ย. 2026)
 *
 * helper บริสุทธิ์ของฟอร์มใน DeviceMovementModal — backend ตรวจซ้ำทุกข้อ (ด่านจริงอยู่ที่
 * crud/device_movement._validate_price_changes) ฝั่งนี้มีไว้บอกผู้ใช้ก่อนกดบันทึก
 *
 * กติกาคิดเงิน: วันที่ ≤ วันสิ้นสุดราคาเดิม → ราคาเดิม · วันที่ ≥ วันเริ่มราคาใหม่ → ราคาใหม่ ·
 * วันระหว่างกลาง → ไม่คิดเงิน
 */

export interface PriceRowInput {
  newRate: string;
  oldEnd: string;   // YYYY-MM-DD
  newStart: string; // YYYY-MM-DD
}

const toUtc = (iso: string): number => {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, (m || 1) - 1, d || 1);
};

/** เลื่อนวันที่แบบ YYYY-MM-DD ไป `days` วัน (คิดแบบ UTC — ไม่โดน DST/timezone ของเครื่อง) */
export const shiftIsoDate = (iso: string, days: number): string => {
  if (!iso) return '';
  const dt = new Date(toUtc(iso) + days * 86_400_000);
  return dt.toISOString().slice(0, 10);
};

/** จำนวนวันที่ไม่คิดเงินระหว่างวันสิ้นสุดราคาเดิมกับวันเริ่มราคาใหม่ (0 = ต่อเนื่อง · ติดลบ = ช่วงซ้อน) */
export const gapDays = (oldEnd: string, newStart: string): number => {
  if (!oldEnd || !newStart) return 0;
  return Math.round((toUtc(newStart) - toUtc(oldEnd)) / 86_400_000) - 1;
};

/** ค่าเริ่มต้นบนฟอร์ม (มติ PO): ราคาเดิมจบ = วันที่มีผล − 1 · ราคาใหม่เริ่ม = วันที่มีผล */
export const defaultPriceDates = (effectiveDate: string): { oldEnd: string; newStart: string } => ({
  oldEnd: shiftIsoDate(effectiveDate, -1),
  newStart: effectiveDate,
});

const RATE_RE = /^\d+(\.\d{1,2})?$/;

/**
 * ข้อความผิดพลาดของแถวปรับราคา (null = ผ่าน)
 * @param lastBilledEnd วันสุดท้ายของรอบที่ออกใบแจ้งหนี้แล้ว — ราคาเดิมต้องจบไม่ก่อนวันนี้
 * @param lastChangeStart วันเริ่มราคาของการปรับครั้งก่อน — ราคาเดิมรอบนี้ต้องจบไม่ก่อนวันนี้
 */
export const validatePriceRow = (
  row: PriceRowInput,
  lastBilledEnd?: string | null,
  lastChangeStart?: string | null,
): string | null => {
  const rate = row.newRate.trim().replace(/,/g, '');
  if (!rate) return 'กรุณาระบุราคาใหม่';
  if (!RATE_RE.test(rate) || Number(rate) <= 0) return 'ราคาใหม่ต้องมากกว่า 0 และมีทศนิยมไม่เกิน 2 ตำแหน่ง';
  if (!row.oldEnd) return 'กรุณาระบุวันสิ้นสุดเก็บเงินราคาเดิม';
  if (!row.newStart) return 'กรุณาระบุวันเริ่มเก็บเงินราคาใหม่';
  if (row.newStart <= row.oldEnd) {
    return 'วันเริ่มเก็บเงินราคาใหม่ต้องอยู่หลังวันสิ้นสุดเก็บเงินราคาเดิม (ห้ามช่วงซ้อนกัน)';
  }
  if (lastBilledEnd && row.oldEnd < lastBilledEnd) {
    return `ออกใบแจ้งหนี้ถึง ${formatThaiDate(lastBilledEnd)} แล้ว — วันสิ้นสุดราคาเดิมต้องไม่ก่อนวันนั้น (ยกเลิกใบแจ้งหนี้ก่อนถ้าต้องการย้อน)`;
  }
  if (lastChangeStart && row.oldEnd < lastChangeStart) {
    return `วันสิ้นสุดราคาเดิมต้องไม่ก่อนวันเริ่มราคาของการปรับครั้งก่อน (${formatThaiDate(lastChangeStart)})`;
  }
  return null;
};

/** 2026-09-14 → 14/09/2026 */
export const formatThaiDate = (iso: string): string => {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

/** แสดงเงิน 1500 → "1,500.00" */
export const formatRate = (v: string | number | null | undefined): string => {
  const n = Number(v ?? 0);
  return Number.isFinite(n)
    ? n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '-';
};
